import { Component, effect, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ChatMessage } from '../chat-message.model';
import { NgForOf, NgIf } from '@angular/common';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { ProductAdvisorService } from './product-advisor.service';
import { FormFieldsModule } from '@app/base-components';
import { DlContentClickService } from 'haba-base-components';
import { UntypedFormBuilder } from '@angular/forms';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { ContactFormComponent } from '../contact-form/contact-form.component';
import { ProductAdvisorProductsListComponent } from '../product-advisor-products-list/product-advisor-products-list.component';
import { PageRobotsMeta, TranslatePipe } from 'haba-core';
import { SeoService } from '@app/services';

@Component({
  selector: 'app-product-advisor',
  imports: [
    NgIf,
    ChatMessageComponent,
    NgForOf,
    FormFieldsModule,
    ProductAdvisorProductsListComponent,
    TranslatePipe,
  ],
  templateUrl: './product-advisor.component.html',
  styleUrl: './product-advisor.component.scss',
})
export class ProductAdvisorComponent implements OnInit {
  @ViewChild('chatBottom', { static: false }) chatBottom!: ElementRef<HTMLElement>;
  @ViewChild('chatMessage') chatMessage: ElementRef;

  @HostListener('document:keydown.enter', ['$event'])
  handleEnter(event: KeyboardEvent) {
    if (this.chatMessage.nativeElement.value) {
      event.preventDefault();
      this.sendMessage(event);
    }
  }

  public productRecommendations: string[];

  private lastOriginChatLength = 0;
  private contactFormDisplayed: boolean = false;

  messages: ChatMessage[] = [];

  loadingMessage: ChatMessage = {
    sender: 'sender',
    content: '',
    timestamp: new Date(),
    isUserMessage: false,
  };

  isSendingNewMessage: boolean = false;

  constructor(
    protected fb: UntypedFormBuilder,
    private readonly productAdvisorService: ProductAdvisorService,
    private seoService: SeoService,
    private dlContentClickService: DlContentClickService
  ) {}

  ngOnInit() {
    this.seoService.setSeoMetaData(
      'Produktberater',
      'Produktberater',
      PageRobotsMeta.NOINDEX,
      PageRobotsMeta.NOFOLLOW
    );

    effect(() => {
      const shouldShow = this.productAdvisorService.showContactForm();

      if (shouldShow) {
        this.showContactForm();
      }
    });
  }

  setRoomLength(roomLength: string) {
    this.productAdvisorService.setRoomX(parseInt(roomLength));
  }

  setRoomWidth(roomWidth: string) {
    this.productAdvisorService.setRoomY(parseInt(roomWidth));
  }

  async sendMessage(event: Event, msg?: string) {
    event.preventDefault();
    event.stopPropagation();
    this.productRecommendations = [];
    const message = msg ? msg : this.chatMessage.nativeElement.value;
    this.chatMessage.nativeElement.value = '';

    if (this.isSendingNewMessage) {
      return;
    }
    this.isSendingNewMessage = true;

    if (!message) {
      console.warn('No chat message');
      return;
    }

    const currentClientMessage: ChatMessage = {
      sender: 'sender',
      content: message,
      timestamp: new Date(),
      isUserMessage: true,
    };

    this.messages.push(currentClientMessage);
    this.chatMessage.nativeElement.value = '';

    setTimeout(() => this.scrollToChatBottom(), 0);

    const response = await this.productAdvisorService.sendMessage(currentClientMessage);

    if (response && this.isSendingNewMessage) {
      const messageHistory = this.productAdvisorService.getChatHistory();

      this.productRecommendations = messageHistory
        .at(messageHistory.length - 1)
        .recommendations?.map((reco) => reco.materialnummer);

      for (let i = this.lastOriginChatLength + 1; i < messageHistory.length; i++) {
        this.messages.push({
          sender: messageHistory[i].role === 'user' ? 'sender' : 'receiver',
          content: messageHistory[i].content,
          timestamp: messageHistory[i].timestamp,
          isUserMessage: messageHistory[i].role === 'user',
        } as unknown as ChatMessage);
      }

      this.isSendingNewMessage = false;
      this.lastOriginChatLength = messageHistory.length;

      setTimeout(() => this.scrollToChatBottom(), 0);
    }
  }

  scrollToChatBottom() {
    const el = this.chatBottom?.nativeElement;
    if (!el) return;

    el.scrollIntoView({
      behavior: 'auto',
      block: 'end',
    });

    setTimeout(() => {
      window.scrollBy({ top: 16, behavior: 'auto' });
    }, 0);
  }

  getWelcomeMessageObj(content: string): ChatMessage {
    return {
      sender: 'receiver',
      content: content,
      timestamp: new Date(),
      isUserMessage: false,
    };
  }

  inConversation() {
    return this.messages.length > 0;
  }

  exportChatToExcel(): void {
    const worksheetData: unknown[][] = this.messages
      .slice(1, this.messages.length)
      .map((message) => [
        message.timestamp,
        message.sender.replace('sender', 'user').replace('receiver', 'product advisor ai'),
        message.content,
      ]);

    const headers = ['Zeitstempel', 'Sender', 'Nachricht'];
    worksheetData.unshift(headers);

    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook: XLSX.WorkBook = {
      Sheets: { Chatverlauf: worksheet },
      SheetNames: ['Chatverlauf'],
    };

    const excelBuffer: ArrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    this.saveAsExcelFile(excelBuffer, 'Chatverlauf');
  }

  private saveAsExcelFile(buffer: ArrayBuffer, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });

    FileSaver.saveAs(data, fileName + '_' + new Date().getTime() + '.xlsx');
  }

  onShowContactFormClick() {
    this.dlContentClickService.trackClick('product_advisor_contact_options');
    this.showContactForm();
  }

  showContactForm() {
    if (this.contactFormDisplayed) {
      return;
    }

    this.messages.push({
      sender: 'receiver',
      content: ContactFormComponent,
      timestamp: new Date(),
      isUserMessage: false,
      showAvatar: false,
    });

    this.contactFormDisplayed = true;
  }

  resetConversation() {
    this.productAdvisorService.resetConversation();
    this.isSendingNewMessage = false;
    this.lastOriginChatLength = 0;
  }

  protected getRoomDimensions() {
    return this.productAdvisorService.getDims();
  }
}
